/**
 * Scroll Lock Utility
 *
 * Prevents body scrolling when modals/sheets are open.
 * Uses position: fixed technique to avoid iOS Safari scroll-to-top bug.
 *
 * Usage:
 *   import { lockBodyScroll, unlockBodyScroll } from './scroll-lock.js';
 *
 *   // When opening modal/sheet
 *   lockBodyScroll();
 *
 *   // When closing modal/sheet
 *   unlockBodyScroll();
 */

// Reference counter for nested modals
let scrollLockCount = 0;

// Saved scroll position to restore after unlock
let savedScrollPosition = 0;

/**
 * Lock body scroll without jumping to top (iOS Safari fix)
 * Uses position: fixed technique instead of just overflow: hidden.
 *
 * On mobile, the scroll root is .mobile-scroll-container (not body) — see
 * mobile-bottom-nav.twig for the iOS viewport fix. In that case, we lock
 * overflow on the container directly and skip the body position hack.
 */
export function lockBodyScroll() {
    if (scrollLockCount === 0) {
        const container = window.mobileScrollContainer;
        if (container) {
            savedScrollPosition = container.scrollTop;
            container.classList.add('scroll-locked');
        } else {
            savedScrollPosition = window.scrollY;
            document.body.style.position = 'fixed';
            document.body.style.top = `-${savedScrollPosition}px`;
            document.body.style.left = '0';
            document.body.style.right = '0';
            document.body.style.width = '100%';
            document.body.style.overflow = 'hidden';
        }
    }
    scrollLockCount++;
}

/**
 * Unlock body scroll and restore previous scroll position
 */
export function unlockBodyScroll() {
    if (scrollLockCount <= 0) {
        scrollLockCount = 0;
        return;
    }
    scrollLockCount--;
    if (scrollLockCount === 0) {
        const container = window.mobileScrollContainer;
        if (container) {
            container.classList.remove('scroll-locked');
            container.scrollTop = savedScrollPosition;
        } else {
            document.body.style.position = '';
            document.body.style.top = '';
            document.body.style.left = '';
            document.body.style.right = '';
            document.body.style.width = '';
            document.body.style.overflow = '';
            window.scrollTo(0, savedScrollPosition);
        }
    }
}

// Sheets that currently hold a lock, by id.
const lockedSheets = new Set();

/**
 * Hold or release one named sheet's lock, at most once per sheet.
 *
 * A sheet drives this from x-effect, which re-runs whenever any value its
 * expression reads changes. A show expression built on a field rather than a
 * boolean stays true across such a change, so without the set the sheet takes a
 * second lock and the single release on close leaves the page locked.
 *
 * @param {string} key sheet id, unique per page
 * @param {boolean} shouldLock
 */
export function syncBodyScroll(key, shouldLock) {
    if (shouldLock) {
        if (lockedSheets.has(key)) {
            return;
        }
        lockedSheets.add(key);
        lockBodyScroll();
        return;
    }
    if (!lockedSheets.delete(key)) {
        return;
    }
    unlockBodyScroll();
}

// Expose globally for inline scripts that can't use ES imports
window.scrollLock = { lock: lockBodyScroll, unlock: unlockBodyScroll, sync: syncBodyScroll };
